import type { TicketType } from '../../domain/types';

export interface CardModel {
  type: TicketType;
  title: string;
  description: string;
  outcome: string;
  signature: string;
  status: string;
  image: ImageBitmap | null;
}

const FONT_STACK = 'system-ui, -apple-system, "Segoe UI", "Microsoft YaHei", "PingFang SC", sans-serif';

export async function renderShareCard(model: CardModel): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 1600;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('无法生成分享卡片');

  context.fillStyle = '#fff9ef';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = model.type === 'reward' ? '#f3b5bb' : '#d8d2d1';
  context.fillRect(0, 0, canvas.width, 220);
  context.fillStyle = '#473638';
  context.font = `800 52px ${FONT_STACK}`;
  context.fillText(model.type === 'reward' ? '奖励单' : '小罚单', 96, 138);

  if (model.image) {
    drawCoverImage(context, model.image, { x: 96, y: 270, width: 1008, height: 520 });
  } else {
    context.fillStyle = '#f4ece3';
    roundedRect(context, 96, 270, 1008, 520, 32);
    context.fill();
    context.fillStyle = '#8b7779';
    context.font = `700 34px ${FONT_STACK}`;
    context.fillText('两小只 · 只保存在本机的分享卡片', 150, 540);
  }

  context.fillStyle = '#473638';
  context.font = `800 68px ${FONT_STACK}`;
  drawWrappedText(context, model.title, 96, 900, 1008, 82, 2);
  if (model.description) {
    context.fillStyle = '#776467';
    context.font = `400 34px ${FONT_STACK}`;
    drawWrappedText(context, model.description, 96, 1045, 1008, 48, 2);
  }

  context.fillStyle = model.type === 'reward' ? '#fce5e7' : '#ece9e8';
  roundedRect(context, 96, 1160, 1008, 220, 32);
  context.fill();
  context.fillStyle = '#473638';
  context.font = `800 30px ${FONT_STACK}`;
  context.fillText(model.type === 'reward' ? '奖励内容' : '惩罚内容', 144, 1220);
  context.font = `700 42px ${FONT_STACK}`;
  drawWrappedText(context, model.outcome, 144, 1290, 912, 54, 2);

  context.fillStyle = '#776467';
  context.font = `600 28px ${FONT_STACK}`;
  context.fillText(`署名：${model.signature}`, 96, 1502);
  context.textAlign = 'right';
  context.fillText(model.status, 1104, 1502);
  context.textAlign = 'left';
  return canvasToBlob(canvas, 'image/jpeg', 0.9);
}

function drawCoverImage(
  context: CanvasRenderingContext2D,
  image: ImageBitmap,
  target: { x: number; y: number; width: number; height: number },
): void {
  const scale = Math.max(target.width / image.width, target.height / image.height);
  const sourceWidth = target.width / scale;
  const sourceHeight = target.height / scale;
  const sourceX = (image.width - sourceWidth) / 2;
  const sourceY = (image.height - sourceHeight) / 2;
  context.save();
  roundedRect(context, target.x, target.y, target.width, target.height, 32);
  context.clip();
  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    target.x,
    target.y,
    target.width,
    target.height,
  );
  context.restore();
}

function drawWrappedText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines: number,
): void {
  const lines: string[] = [];
  let line = '';
  for (const character of text) {
    const candidate = line + character;
    if (line && context.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = character;
      if (lines.length === maxLines) break;
    } else {
      line = candidate;
    }
  }
  if (lines.length < maxLines && line) lines.push(line);
  lines.slice(0, maxLines).forEach((value, index) => context.fillText(value, x, y + index * lineHeight));
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('无法生成分享卡片'));
    }, type, quality);
  });
}
